import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import isObject from 'lodash/isObject';
import { MdOutlineUnpublished } from 'react-icons/md';
import urlPageLocalNotifications from '../../../../../../urls/urlPageLocalNotifications';
import urlPageLocalNotificationDetails from '../../../../../../urls/urlPageLocalNotificationDetails';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import useModifiedDynamicDataErrors from '../../../../../../app/hooks/useModifiedDynamicDataErrors';
import { fieldTypeToValidationRule, TYPE_IMAGE, TYPE_OBJECT } from '../../../../../Template builder/const/templates';
import isFieldRequired from '../../../../../Content/utils/isFieldRequired';
import Yup from '../../../../../../vendor/yup';
import { mkValidationSchema } from '../../../../forms/FormCreateLocalNotification/FormCreateLocalNotification.const';
import createFileFromURL from '../../../../../../lib/createFileFromURL';
import handleResponse from '../../../../../../lib/handleResponse';
import transformErrors from '../../../../../../lib/transformErrors';
import ActionButton from '../../../../../../layout/components/action-button';
import { HasRights } from '../../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../../const/permissions';
import FormEditLocalNotification from '../../../../forms/FormEditLocalNotification';
import {
  useGetSpecificLocalNotificationQuery,
  useUnpublishLocalNotificationMutation,
  useUpdateLocalNotificationMutation,
} from '../../../../api/localNotificationsApiSlice';



let validation = {};

const PageEditLocalNotification = () => {
  const intl = useIntl();
  const language = useSelector(({ customise: { language } }) => language);
  const { notificationId } = useParams();

  const navigate = useNavigate();
  const [ initValues, setInitValues ] = useState({ data: {} });
  const [ formErrors, setFormErrors ] = useState({});

  const { data: localNotification = {}, isFetching } = useGetSpecificLocalNotificationQuery({ notificationId, includes: '?include=dynamic_data' });
  const [ updateLocalNotification, { isLoading: isUpdatingLocalNotification } ] = useUpdateLocalNotificationMutation();
  const [ unpublishLocalNotification, { isLoading: isUnpublishLocalNotificationLoading } ] = useUnpublishLocalNotificationMutation();

  const modifiedErrors = useModifiedDynamicDataErrors(
    localNotification?.template?.data.id,
    localNotification?.template?.data,
    formErrors,
    language,
  );

  const getDynamicData = (initialValues, detailsData) => {
    const data = {};
    const dataSchema = {};

    detailsData?.template?.data?.fields?.data.filter((field) => field.type !== TYPE_OBJECT)
      .forEach(async (field) => {
        const localNotificationField = detailsData?.dynamic_data?.data?.find((item) => item.template_field_id === field.id);

        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        if (field.type === TYPE_IMAGE) {
          if (localNotificationField?.value) {
            const fileName = localNotificationField.value?.split('/').pop();
            const file = await createFileFromURL(localNotificationField?.value, fileName);

            data[field.full_name] = {
              value: [ {
                uid: '-1',
                name: localNotificationField?.value,
                thumbUrl: localNotificationField?.value,
                status: 'done',
                originFileObj: file,
                url: localNotificationField?.value,
              } ],
              template_field_id: field.id,
            };
          } else {
            data[field.full_name] = {
              value: [],
              template_field_id: field.id,
            };
          }
        } else {
          data[field.full_name] = {
            value: localNotificationField?.value,
            template_field_id: field.id,
          };
        }

        setInitValues((prevState) => ({ ...prevState, data }));
      });

    validation = mkValidationSchema({
      data: Yup.object().shape(dataSchema),
    });

    return data;
  };

  useEffect(() => {
    const initialValues = {
      title: localNotification?.title,
      is_published: false,
      updated_at: localNotification?.updated_at,
      published_at: localNotification?.published_at,
      template_id: localNotification?.template?.data?.id,
      data: {},
    };

    setInitValues(initialValues);
    getDynamicData(initialValues, localNotification);
  }, [ isFetching ]);

  const handleUnpublish = () => {
    unpublishLocalNotification(notificationId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const onSubmit = (data) => {
    updateLocalNotification({ id: notificationId, values: data })
      .unwrap()
      .then(() => handleResponse(
        'success',
        intl,
        'ui-general-updated-successfully',
        [ () => navigate(urlPageLocalNotifications()) ],
      ))
      .catch((error) => handleResponse(
        'error',
        intl,
        'ui-general-update-failed',
        [ () => {
          if (isObject(error?.data?.errors)) {
            setFormErrors(transformErrors(error?.data?.errors));
          }
        } ],
        error,
      ));
  };

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageLocalNotifications()}>
              <IntlMessages id='local-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbParent2={
            <Link to={urlPageLocalNotificationDetails({ notificationId })}>
              {notificationId}
            </Link>
          }
          breadCrumbActive={<IntlMessages id='ui-general-edit' />}
        />

        <Col md={12} span={24}>
          <Row
            gutter={[ 8, 8 ]}
            justify="end"
            align="middle"
          >
            {Boolean(localNotification?.is_published) && HasRights([ Permissions.OTHER.NOTIFICATIONS.LOCAL.UNPUBLISH ]) && (
              <Col>
                <Space>
                  <ActionButton
                    loading={isUnpublishLocalNotificationLoading}
                    title={intl.formatMessage({ id: 'ui-general-unpublish' })}
                    icon={<MdOutlineUnpublished className="hp-mr-8" size={18} />}
                    onClick={handleUnpublish}
                  />
                </Space>
              </Col>
            )}
            <Col>
              <Space>
                <Link to={urlPageLocalNotifications()}>
                  <Button
                    type="primary"
                    className="hp-mr-sm-8"
                    ghost="true"
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Link>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <FormEditLocalNotification
              initialErrors={modifiedErrors}
              initialValues={initValues}
              templateFields={localNotification?.template?.data?.fields?.data || []}
              templateOptions={[ {
                label: localNotification?.template?.data?.title,
                value: localNotification?.template?.data?.id,
              } ]}
              validationSchema={validation}
              isSubmitting={isFetching || isUnpublishLocalNotificationLoading || isUpdatingLocalNotification}
              onCancel={() => {}}
              onSubmit={onSubmit}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageEditLocalNotification;
